import { useState, useEffect, useCallback } from 'react'
import { FiPhone, FiPlus, FiTrash2, FiCopy, FiCheck, FiLoader } from 'react-icons/fi'
import ConfirmDialog from './ConfirmDialog'

// Telefone adicional na seção INFORMAÇÕES do Conversas (16/10/2026, pedido do
// diretor): na conversa a pessoa avisa o número novo e ele anota aqui. Fica no
// banco do fin (contato_telefone_extra), ligado ao código DRACO quando há — assim
// aparece em qualquer conversa da mesma pessoa. Não altera o cadastro do DRACO.

type Fetch = (url: string, options?: RequestInit) => Promise<Response>
interface Item { id: number; telefone: string; observacao: string | null; criado_por: string | null; criado_em: string }

const fmtTel = (t: string) => {
  const d = t.replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '')
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return t
}
const fmtData = (s: string) => new Date(s).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })

export function TelefonesExtra({ phone, codigo, authenticatedFetch }: { phone: string | null; codigo: string | null; authenticatedFetch: Fetch }) {
  const [itens, setItens] = useState<Item[]>([])
  const [abrindo, setAbrindo] = useState(false)
  const [tel, setTel] = useState('')
  const [obs, setObs] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [copiado, setCopiado] = useState<number | null>(null)
  const [apagar, setApagar] = useState<Item | null>(null)
  const phoneLimpo = (phone || '').replace(/\D/g, '').replace(/@.*/, '')

  const carregar = useCallback(async () => {
    if (!phoneLimpo && !codigo) { setItens([]); return }
    const qs = new URLSearchParams({ phone: phoneLimpo, ...(codigo ? { codigo } : {}) })
    try {
      const r = await authenticatedFetch(`/api/telefone-extra?${qs}`)
      const d = await r.json()
      setItens(Array.isArray(d.items) ? d.items : [])
    } catch { /* lista some, campo continua */ }
  }, [phoneLimpo, codigo, authenticatedFetch])

  useEffect(() => { setAbrindo(false); setTel(''); setObs(''); setErro(null); carregar() }, [carregar])

  const salvar = async () => {
    setSalvando(true); setErro(null)
    try {
      const r = await authenticatedFetch('/api/telefone-extra', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ telefone: tel, observacao: obs, phone: phoneLimpo, codigo }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || `Erro ${r.status}`)
      setTel(''); setObs(''); setAbrindo(false)
      await carregar()
    } catch (e) { setErro(e instanceof Error ? e.message : 'Erro ao salvar') }
    finally { setSalvando(false) }
  }

  const remover = async (it: Item) => {
    setApagar(null)
    const r = await authenticatedFetch(`/api/telefone-extra/${it.id}`, { method: 'DELETE' })
    if (!r.ok) { const d = await r.json().catch(() => ({})); setErro(d.error || 'Não consegui remover'); return }
    await carregar()
  }

  const copiar = async (it: Item) => {
    try { await navigator.clipboard.writeText(it.telefone); setCopiado(it.id); setTimeout(() => setCopiado(null), 1500) } catch { /* clipboard bloqueado */ }
  }

  if (!phoneLimpo && !codigo) return null
  const digitos = tel.replace(/\D/g, '').length

  return (
    <div className="pt-1 space-y-1">
      {itens.map(it => (
        <div key={it.id} className="group flex items-center gap-2 text-[11px]"
          title={`Anotado${it.criado_por ? ` por ${it.criado_por}` : ''} em ${fmtData(it.criado_em)}`}>
          <FiPhone className="w-3 h-3 text-muted-foreground shrink-0" />
          <span className="text-muted-foreground shrink-0">Adicional:</span>
          <span className="text-foreground font-medium tabular-nums">{fmtTel(it.telefone)}</span>
          {it.observacao && <span className="text-muted-foreground/70 truncate">· {it.observacao}</span>}
          <span className="flex-1" />
          <button onClick={() => copiar(it)} title="Copiar o número" className="p-0.5 rounded text-muted-foreground hover:text-foreground">
            {copiado === it.id ? <FiCheck className="w-3 h-3 text-emerald-500" /> : <FiCopy className="w-3 h-3" />}
          </button>
          <button onClick={() => setApagar(it)} title="Remover" className="p-0.5 rounded text-muted-foreground/50 hover:text-red-500">
            <FiTrash2 className="w-3 h-3" />
          </button>
        </div>
      ))}

      {abrindo ? (
        <div className="space-y-1 rounded-md border border-border p-1.5">
          <input autoFocus value={tel} onChange={e => setTel(e.target.value)} inputMode="tel" placeholder="(67) 99999-9999"
            onKeyDown={e => { if (e.key === 'Enter' && digitos >= 10) salvar(); if (e.key === 'Escape') setAbrindo(false) }}
            className="w-full text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500" />
          <input value={obs} onChange={e => setObs(e.target.value)} maxLength={120} placeholder="Observação (opcional) — ex.: número novo, do marido"
            onKeyDown={e => { if (e.key === 'Enter' && digitos >= 10) salvar(); if (e.key === 'Escape') setAbrindo(false) }}
            className="w-full text-[11px] px-2 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500" />
          {erro && <div className="text-[10px] text-red-500">{erro}</div>}
          <div className="flex gap-1.5">
            <button onClick={() => { setAbrindo(false); setErro(null) }} className="flex-1 text-[10px] text-muted-foreground hover:text-foreground py-1">Cancelar</button>
            <button onClick={salvar} disabled={salvando || digitos < 10 || digitos > 13}
              className="flex-1 inline-flex items-center justify-center gap-1 rounded bg-primary text-primary-foreground text-[10px] font-medium py-1 disabled:opacity-50">
              {salvando && <FiLoader className="w-3 h-3 animate-spin" />} Salvar telefone
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAbrindo(true)}
          className="inline-flex items-center gap-1 text-[10px] text-primary hover:underline">
          <FiPlus className="w-3 h-3" /> Adicionar telefone
        </button>
      )}
      {!abrindo && erro && <div className="text-[10px] text-red-500">{erro}</div>}

      <ConfirmDialog isOpen={!!apagar} type="danger" portal
        title="Remover telefone adicional?"
        message={apagar ? `${fmtTel(apagar.telefone)} sai da ficha desta pessoa.` : ''}
        confirmText="Remover" cancelText="Manter"
        onConfirm={() => apagar && remover(apagar)} onCancel={() => setApagar(null)} />
    </div>
  )
}
